/**
 * 前端 API 内存缓存工具
 *
 * 用于缓存不常变化的参考数据（分类、配置项、轮播图等），
 * 避免每次页面刷新/切换时重复请求相同的全量接口。
 *
 * 特性：
 * - TTL 过期（默认 10 分钟）
 * - 请求去重：同一 key 的并发请求共享一个 in-flight Promise
 * - 自动 GC：过期条目惰性删除
 */

// 默认缓存时长（毫秒）
var DEFAULT_TTL = 10 * 60 * 1000

// 缓存存储：{ key: { promise, result, timestamp, ttl } }
var store = {}

/**
 * 生成缓存 key
 * @param {string} url  请求 URL
 * @param {object} params 请求参数
 * @returns {string}
 */
function makeKey(url, params) {
  if (params && typeof params === 'object' && Object.keys(params).length > 0) {
    return url + '?' + JSON.stringify(params)
  }
  return url
}

/**
 * 判断缓存条目是否有效
 */
function isValid(entry) {
  if (!entry) return false
  return Date.now() - entry.timestamp < entry.ttl
}

/**
 * 创建带缓存的 API 调用
 *
 * @param {Function} apiFn   原始 API 函数（返回 Promise）
 * @param {number}   [ttl]   缓存有效期（毫秒），默认 10 分钟
 * @param {Function} [keyFn] 自定义 key 生成函数，接收 (url, params)，返回 string
 * @returns {Function} 与原函数签名一致的缓存版函数
 *
 * 用法：
 *   var cachedGet = createCachedApi(getProductType, 5 * 60 * 1000)
 *   cachedGet({ pageSize: 0 })  // 首次请求，走网络
 *   cachedGet({ pageSize: 0 })  // 5 分钟内走缓存
 */
export function createCachedApi(apiFn, ttl, keyFn) {
  var cacheTTL = ttl || DEFAULT_TTL

  return function() {
    // 从 arguments 推断 url 和 params
    var config = apiFn.apply(null, arguments)
    // config 是 axios 配置对象 { url, method, params/data }
    if (!config || !config.url) {
      return apiFn.apply(null, arguments)
    }

    var params = config.params || config.data || {}
    var key = keyFn ? keyFn(config.url, params) : makeKey(config.url, params)

    var entry = store[key]

    // 缓存命中且未过期
    if (entry && isValid(entry)) {
      // 如果结果已返回（非 pending），直接返回
      if (entry.result) {
        return Promise.resolve(entry.result)
      }
      // 如果有 in-flight promise，共享它（请求去重）
      if (entry.promise) {
        return entry.promise
      }
    }

    // 缓存未命中或已过期，发起新请求
    var promise = apiFn.apply(null, arguments)
      .then(function(res) {
        var e = store[key]
        if (e) {
          e.result = res
          e.promise = null
          e.timestamp = Date.now()
          e.ttl = cacheTTL
        }
        return res
      })
      .catch(function(err) {
        // 请求失败时删除缓存条目，下次重新请求
        delete store[key]
        throw err
      })

    store[key] = {
      promise: promise,
      result: null,
      timestamp: Date.now(),
      ttl: cacheTTL
    }

    return promise
  }
}

/**
 * 手动清除指定 key 的缓存
 * @param {string} url
 * @param {object} [params]
 */
export function clearCache(url, params) {
  var key = makeKey(url, params)
  delete store[key]
}

/**
 * 清除所有缓存
 */
export function clearAllCache() {
  store = {}
}

/**
 * 惰性 GC：清理所有过期条目（可在路由切换时调用）
 */
export function gcCache() {
  var now = Date.now()
  Object.keys(store).forEach(function(key) {
    var entry = store[key]
    if (entry && now - entry.timestamp >= entry.ttl) {
      delete store[key]
    }
  })
}
